import { ApplicationElement, html } from '@crossiety/lit-components';
import { ifDefined } from 'lit/directives/if-defined.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';
import '@crossiety/lit-components/components/icon/element.js';
import translations from './translations.js';
import style from './style.lit.css';

/**
 * Tooltips display additional information.
 * Can have header and body text or can be used with the ```content``` slot to create tooltips with HTML content.
 *
 * @prop header - Will render tooltip with header (optional)
 * @prop body - For tooltip content (optional if used content slot)
 * @prop trigger - Set the custom trigger attribute to toggle the tooltip (optional) (options: hover, focus, click)
 * @prop hoist - Prevent tooltips being clipped if they’re inside a container.
 * @prop hasHeaderSlotContent - Internal property => For hiding header slot
 *
 * @slot default - to change default trigger content
 * @slot header - to create tooltips with HTML content
 * @slot body - to create tooltips with HTML content
 *
 * @cssprop [--trigger-color: var(--dark-text)] - Text color of trigger
 * @cssprop [--trigger-font-size: 18px] - Font size of trigger
 * @cssprop [--trigger-font-weight: 400] - Font weight of trigger
 * @cssprop [--trigger-padding: 4px] - Padding of trigger
 * @cssprop [--content-border: none] - Border of content
 * @cssprop [--content-border-radius: 0] - Border radius of content
 * @cssprop [--content-body-shadow: none] - Box shadow of content
 * @cssprop [--header-padding: 0] - Padding of header
 * @cssprop [--header-font-size: 1.3em] - Font size of header
 * @cssprop [--header-font-weight: 400] - Font weight of header
 * @cssprop [--header-background: transparent] - Background of header
 * @cssprop [--header-border-bottom: none] - Bottom border of header
 * @cssprop [--header-color: inherit] - Text color of header
 * @cssprop [--body-padding: 0] - Padding of body
 * @cssprop [--body-font-size: 1em] - Font size of body
 * @cssprop [--body-font-weight: 400] - Font weight of body
 * @cssprop [--body-background: var(--sl-tooltip-background-color)] - Background color of body
 * @cssprop [--body-color: inherit] - Text color of body
 */

export default class Tooltip extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    header: { type: String },
    body: { type: String },
    trigger: { type: String },
    hoist: { type: Boolean },
    hasHeaderSlotContent: { type: Boolean, state: true },
  };

  /** @internal */
  static translations = translations;

  constructor() {
    super();
    this.trigger = 'click';
  }

  /** @internal */
  get shouldShowHeaderSlot() {
    return this.header || this.hasHeaderSlotContent;
  }

  /** @internal */
  get tooltip() {
    return this.shadowRoot.querySelector('sl-tooltip');
  }

  handleEvent(e) {
    e.type === 'click' && this.handleBodyClick();
  }

  handleTooltipAfterShow() {
    document.addEventListener('click', this);
  }

  handleTooltipAfterHide() {
    document.removeEventListener('click', this);
  }

  handleBodyClick() {
    this.hide();
  }

  handleHeaderSlotChange(e) {
    const childNodes = e.target.assignedNodes({ flatten: true });

    this.hasHeaderSlotContent = childNodes.length > 0;
  }

  hide() {
    this.tooltip.hide();
  }

  render() {
    return html`
      <sl-tooltip
        trigger=${ifDefined(this.trigger)}
        ?hoist=${this.hoist}
        @sl-after-show=${this.handleTooltipAfterShow}
        @sl-after-hide=${this.handleTooltipAfterHide}>
        <button class="trigger" aria-label=${this.t('toggle_tooltip')}>
          <slot>
            <ef-icon name="info-circle"></ef-icon>
          </slot>
        </button>
        <slot
          slot="content"
          name="header"
          ?hidden=${!this.shouldShowHeaderSlot}
          @slotchange=${this.handleHeaderSlotChange}>
          ${this.header}
        </slot>
        <slot slot="content" name="body"> ${this.body} </slot>
      </sl-tooltip>
    `;
  }
}

Tooltip.registerTagName('xs-tooltip');

customElements.get('xs-tooltip') ||
  customElements.define('xs-tooltip', Tooltip);
